import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { MessageV2DTO, PartnerDTO } from 'support/types';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { DocumentProperties, DocumentPropertiesItem } from 'components/Sidebar/SidebarProperties/SidebarProperties';
import { TradePartnerNameAndLogo } from 'components/TradePartnerNameAndLogo';

import { PaymentReconciliationDetailsData } from '../../types';
import { PaymentReconciliationSidebarStatusBadge } from './components/PaymentReconciliationSidebarStatusBadge/PaymentReconciliationSidebarStatusBadge';
import { PaymentReconciliationSummary } from './components/PaymentReconciliationSummary/PaymentReconciliationSummary';

type PaymentReconciliationDetailsSidebarProps = {
  message: MessageV2DTO | undefined;
  currentPartner: PartnerDTO | undefined;
  paymentReconciliation: PaymentReconciliationDetailsData | undefined;
};
export const PaymentReconciliationDetailsSidebar: FC<PaymentReconciliationDetailsSidebarProps> = ({
  message,
  currentPartner,
  paymentReconciliation,
}) => {
  const { t } = useTranslation();
  const senderPartner = message?.sender_partner;
  const receiverPartner = message?.receiver_partner;
  const paymentData = paymentReconciliation?.data;

  const otherPartner = currentPartner && senderPartner?.id === currentPartner?.id ? receiverPartner : senderPartner;
  const hasAllNeededData = message && currentPartner && paymentReconciliation;

  const properties: Array<DocumentPropertiesItem> = [
    {
      label: t('webedi:sidebar.properties.partner'),
      key: 'partner',
      value: (
        <TradePartnerNameAndLogo
          partner={otherPartner}
          size="small"
          textConfiguration={{ className: 'text-inherit' }}
          logoConfiguration={{ className: 'rounded-full' }}
        />
      ),
    },
    {
      label: t('webedi:sidebar.properties.status'),
      key: 'status',
      value:
        message && paymentData?.receiver_envelope ? (
          <PaymentReconciliationSidebarStatusBadge
            message={message}
            receiverEnvelope={paymentData.receiver_envelope}
            currentPartner={currentPartner}
          />
        ) : null,
    },
    {
      label: t('webedi:sidebar.properties.documentDate'),
      key: 'document-date',
      value: message ? formatDayAndTime(message.created_at) : EMPTY_FIELD,
    },
    {
      key: 'payment-method',
      label: t('paymentReconciliation:sidebar.properties.paymentMethod'),
      value: paymentData?.payment_method || EMPTY_FIELD,
    },
    {
      key: 'payment-reference',
      label: t('paymentReconciliation:sidebar.properties.paymentReference'),
      value: paymentData?.payment_reference || EMPTY_FIELD,
    },
  ];

  return (
    <Sidebar bottomSlot={paymentData ? <PaymentReconciliationSummary paymentReconciliation={paymentData} /> : null}>
      {hasAllNeededData ? <DocumentProperties properties={properties} /> : null}
    </Sidebar>
  );
};
